import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    BadgeCheck, BriefcaseBusiness, Check, ChevronRight, CircleDotDashed,
    Cpu, GraduationCap, HeartHandshake, HeartPulse, Landmark, Layers3,
    Leaf, Lightbulb, MoonStar, Palette, Pencil, Plus, Search, ShieldCheck,
    SlidersHorizontal, Sparkles, Tag, Trash2, UserRound, UsersRound,
    Wrench, X,
} from 'lucide-react';
import Sidebar from '../../components/Sidebar';
import BrandLoader from '../../components/BrandLoader';
import Navbar from '../../components/Navbar';
import ConfirmDialog from '../../components/ConfirmDialog';
import Select from '../../components/Select';
import API from '../../utils/api';
import toast from 'react-hot-toast';
import './ProfileOptions.css';

const CATEGORIES = [
    { key: 'religion', label: 'Religion', description: 'Faith and community options', Icon: Sparkles },
    { key: 'caste', label: 'Caste', description: 'Groups linked to a religion', parentOf: 'religion', Icon: UsersRound },
    { key: 'subcaste', label: 'Sub Caste', description: 'Lineages linked to caste', parentOf: 'caste', Icon: Layers3 },
    { key: 'maritalstatus', label: 'Marital Status', description: 'Relationship status choices', Icon: HeartHandshake },
    { key: 'job', label: 'Occupation', description: 'Professions arranged by industry', Icon: BriefcaseBusiness },
    { key: 'rasi', label: 'Rasi', description: 'Zodiac sign options', Icon: CircleDotDashed },
    { key: 'nakshatra', label: 'Nakshatra', description: 'Birth star options', Icon: MoonStar },
    { key: 'dosham', label: 'Dosham', description: 'Horoscope matching choices', Icon: ShieldCheck },
    { key: 'doshamtype', label: 'Dosham Type', description: 'Specific Dosham shown when Dosham is Yes', Icon: ShieldCheck },
    { key: 'familystatus', label: 'Family Status', description: 'Middle class, affluent...', Icon: UsersRound },
    { key: 'familytype', label: 'Family Type', description: 'Nuclear, joint...', Icon: UsersRound },
    { key: 'marriageurgency', label: 'Marriage Urgency', description: 'Within 1 month, 3 months, 6 months...', Icon: HeartHandshake },
    { key: 'bodytype', label: 'Body Type', description: 'Slim, average, athletic...', Icon: UserRound },
    { key: 'networth', label: 'Net Worth', description: 'Ranges like 5 - 10 Lakhs', Icon: BadgeCheck },
    { key: 'assettype', label: 'Asset Types', description: 'House, land, car, gold...', Icon: BadgeCheck },
    { key: 'familyvalues', label: 'Family Values', description: 'Orthodox, traditional, moderate, liberal...', Icon: UsersRound },
];

const PENDING_CATEGORIES = [
    ...CATEGORIES,
    { key: 'state', label: 'State / UT' },
    { key: 'district', label: 'District' },
];

const OCCUPATION_GROUPS = [
    { key: 'technology', label: 'Technology & Engineering', Icon: Cpu, values: ['Software Engineer', 'Civil Engineer', 'Mechanical Engineer', 'Data Analyst'] },
    { key: 'healthcare', label: 'Healthcare & Medicine', Icon: HeartPulse, values: ['Doctor', 'Nurse', 'Pharmacist'] },
    { key: 'education', label: 'Education & Academia', Icon: GraduationCap, values: ['Teacher', 'Professor', 'Student'] },
    { key: 'business', label: 'Business & Finance', Icon: BriefcaseBusiness, values: ['Business Owner', 'Accountant', 'Bank Employee', 'Sales Executive', 'Marketing Executive', 'HR Executive', 'Shop Owner', 'Self Employed'] },
    { key: 'government', label: 'Government & Defence', Icon: Landmark, values: ['Government Employee', 'Police', 'Army/Defence'] },
    { key: 'creative', label: 'Creative & Design', Icon: Palette, values: ['Designer', 'Architect', 'Tailor'] },
    { key: 'trades', label: 'Skilled Trades & Services', Icon: Wrench, values: ['Electrician', 'Driver'] },
    { key: 'agriculture', label: 'Agriculture', Icon: Leaf, values: ['Farmer'] },
    { key: 'other', label: 'Home & Other', Icon: UserRound, values: ['Homemaker', 'Not Employed'] },
];

const groupForOccupation = option => {
    const storedGroup = OCCUPATION_GROUPS.find(group => group.key === option.parent);
    if (storedGroup) return storedGroup.key;
    const exactGroup = OCCUPATION_GROUPS.find(group => group.values.some(value => value.toLowerCase() === option.value.toLowerCase()));
    if (exactGroup) return exactGroup.key;
    const value = option.value.toLowerCase();
    if (/(software|developer|engineer|technology|data|analyst|it\b)/.test(value)) return 'technology';
    if (/(doctor|nurse|medical|health|pharma|therap)/.test(value)) return 'healthcare';
    if (/(teacher|professor|lecturer|student|education|tutor)/.test(value)) return 'education';
    if (/(business|account|bank|finance|sales|marketing|manager|executive|entrepreneur)/.test(value)) return 'business';
    if (/(government|police|army|defence|civil service)/.test(value)) return 'government';
    if (/(design|artist|architect|writer|media|tailor)/.test(value)) return 'creative';
    if (/(electrician|driver|mechanic|technician|plumber|carpenter)/.test(value)) return 'trades';
    if (/(farmer|agri)/.test(value)) return 'agriculture';
    return 'other';
};

const ProfileOptions = () => {
    const [activeTab, setActiveTab] = useState('manage');
    const [category, setCategory] = useState('religion');
    const [religionParent, setReligionParent] = useState('');
    const [casteParent, setCasteParent] = useState('');
    const [religionChoices, setReligionChoices] = useState([]);
    const [casteChoices, setCasteChoices] = useState([]);
    const [options, setOptions] = useState([]);
    const [newValue, setNewValue] = useState('');
    const [occupationGroup, setOccupationGroup] = useState('technology');
    const [search, setSearch] = useState('');
    const [editingId, setEditingId] = useState(null);
    const [editingValue, setEditingValue] = useState('');
    const [editingGroup, setEditingGroup] = useState('other');
    const [loading, setLoading] = useState(false);
    const [pending, setPending] = useState([]);
    const [pendingLoading, setPendingLoading] = useState(false);
    const [pendingConfirm, setPendingConfirm] = useState(null);
    const [pendingEditingId, setPendingEditingId] = useState(null);
    const [pendingEditingValue, setPendingEditingValue] = useState('');
    const [pendingSaving, setPendingSaving] = useState(false);

    const categoryConfig = CATEGORIES.find(item => item.key === category);
    const visibleOptions = useMemo(() => {
        const query = search.trim().toLowerCase();
        return options.filter(option => !query || option.value.toLowerCase().includes(query));
    }, [options, search]);

    const occupationSections = useMemo(() => OCCUPATION_GROUPS.map(group => ({
        ...group,
        options: visibleOptions.filter(option => groupForOccupation(option) === group.key),
        total: options.filter(option => groupForOccupation(option) === group.key).length,
    })).filter(group => group.options.length || !search), [options, visibleOptions, search]);

    const resolveParent = () => {
        if (category === 'caste') return religionParent || null;
        if (category === 'subcaste') return religionParent && casteParent ? `${religionParent}|${casteParent}` : null;
        return null;
    };

    const cascadeReady = () => {
        if (category === 'caste') return Boolean(religionParent);
        if (category === 'subcaste') return Boolean(religionParent && casteParent);
        return true;
    };

    useEffect(() => {
        if (category === 'caste' || category === 'subcaste') {
            API.get('/admin/options', { params: { category: 'religion', status: 'active' } })
                .then(response => setReligionChoices(response.data.options.map(option => option.value)))
                .catch(() => {});
        }
    }, [category]);

    useEffect(() => {
        if (category === 'subcaste' && religionParent) {
            API.get('/admin/options', { params: { category: 'caste', parent: religionParent, status: 'active' } })
                .then(response => setCasteChoices(response.data.options.map(option => option.value)))
                .catch(() => {});
        } else {
            setCasteChoices([]);
        }
    }, [category, religionParent]);

    const fetchOptions = useCallback(async () => {
        if (!cascadeReady()) { setOptions([]); return; }
        setLoading(true);
        try {
            const parent = resolveParent();
            const params = { category, status: 'active' };
            if (category === 'caste' || category === 'subcaste') params.parent = parent;
            const response = await API.get('/admin/options', { params });
            setOptions(response.data.options || []);
        } catch (error) {
            toast.error('Failed to load options');
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [category, religionParent, casteParent]);

    useEffect(() => { fetchOptions(); }, [fetchOptions]);

    const fetchPending = useCallback(async () => {
        setPendingLoading(true);
        try {
            const results = await Promise.all(PENDING_CATEGORIES.map(item => API.get('/admin/options', { params: { category: item.key, status: 'pending' } })));
            const all = results.flatMap(result => result.data.options || []).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
            setPending(all);
        } catch (error) {
            toast.error('Failed to load pending suggestions');
        } finally {
            setPendingLoading(false);
        }
    }, []);

    useEffect(() => { fetchPending(); }, [fetchPending]);
    useEffect(() => { if (activeTab === 'pending') fetchPending(); }, [activeTab, fetchPending]);

    const selectCategory = key => {
        setCategory(key);
        setReligionParent('');
        setCasteParent('');
        setSearch('');
        setNewValue('');
        setEditingId(null);
    };

    const handleAdd = async event => {
        event.preventDefault();
        if (!newValue.trim()) return;
        try {
            const parent = category === 'job' ? occupationGroup : resolveParent();
            await API.post('/admin/options', { category, value: newValue.trim(), parent });
            toast.success(`Added “${newValue.trim()}”`);
            setNewValue('');
            fetchOptions();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to add');
        }
    };

    const startEdit = option => { setEditingId(option._id); setEditingValue(option.value); setEditingGroup(groupForOccupation(option)); };
    const cancelEdit = () => { setEditingId(null); setEditingValue(''); setEditingGroup('other'); };

    const handleSaveRename = async id => {
        if (!editingValue.trim()) return;
        try {
            await API.put(`/admin/options/${id}`, {
                value: editingValue.trim(),
                ...(category === 'job' && { parent: editingGroup }),
            });
            toast.success('Option renamed everywhere');
            cancelEdit();
            fetchOptions();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to rename');
        }
    };

    const confirmDeleteOption = async () => {
        const target = pendingConfirm;
        setPendingConfirm(null);
        try {
            await API.delete(`/admin/options/${target.id}`);
            toast.success('Option removed');
            fetchOptions();
        } catch (error) {
            toast.error('Failed to remove');
        }
    };

    const handleApprove = async item => {
        try {
            await API.put(`/admin/options/${item._id}/approve`);
            toast.success(`“${item.value}” is now live`);
            fetchPending();
            if (item.category === category) fetchOptions();
        } catch (error) {
            toast.error('Failed to approve');
        }
    };

    const startPendingEdit = item => {
        setPendingEditingId(item._id);
        setPendingEditingValue(item.value);
    };

    const cancelPendingEdit = () => {
        setPendingEditingId(null);
        setPendingEditingValue('');
    };

    const handleSavePendingEdit = async item => {
        const value = pendingEditingValue.trim();
        if (!value) {
            toast.error('Suggestion value cannot be empty');
            return;
        }
        if (value === item.value) {
            cancelPendingEdit();
            return;
        }
        setPendingSaving(true);
        try {
            const response = await API.put(`/admin/options/${item._id}`, { value });
            setPending(current => current.map(option => option._id === item._id ? response.data.option : option));
            toast.success('Suggestion updated. Review it, then approve when ready.');
            cancelPendingEdit();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to update suggestion');
        } finally {
            setPendingSaving(false);
        }
    };

    const confirmRejectSuggestion = async () => {
        const target = pendingConfirm;
        setPendingConfirm(null);
        try {
            await API.delete(`/admin/options/${target.id}`);
            toast.success('Suggestion rejected');
            fetchPending();
        } catch (error) {
            toast.error('Failed to reject');
        }
    };

    const renderOption = option => (
        <div className={`profile-option-item ${editingId === option._id ? 'is-editing' : ''} ${editingId === option._id && category === 'job' ? 'is-job-editing' : ''}`} key={option._id}>
            {editingId === option._id ? (
                <>
                    <input autoFocus value={editingValue} onChange={event => setEditingValue(event.target.value)}
                        onKeyDown={event => { if (event.key === 'Enter') handleSaveRename(option._id); if (event.key === 'Escape') cancelEdit(); }} />
                    {category === 'job' && (
                        <div className="profile-option-group-edit">
                            <Select value={editingGroup} onChange={setEditingGroup} options={OCCUPATION_GROUPS.map(group => ({ value: group.key, label: group.label }))} allowEmpty={false} ariaLabel="Profession category" />
                        </div>
                    )}
                    <button className="is-save" onClick={() => handleSaveRename(option._id)} title="Save"><Check size={14} /></button>
                    <button className="is-delete" onClick={cancelEdit} title="Cancel"><X size={14} /></button>
                </>
            ) : (
                <>
                    <span>{option.value}</span>
                    <div className="profile-option-item__actions">
                        <button className="is-edit" onClick={() => startEdit(option)} title="Rename" aria-label={`Rename ${option.value}`}><Pencil size={13} /></button>
                        <button className="is-delete" onClick={() => setPendingConfirm({ type: 'delete', id: option._id, value: option.value })} title="Remove"><Trash2 size={14} /></button>
                    </div>
                </>
            )}
        </div>
    );

    return (
        <div className="profile-options-layout">
            <Sidebar />
            <main className="admin-content profile-options-content">
                <Navbar />
                <div className="profile-options-page">
                    <header className="profile-options-heading">
                        <div>
                            <div className="profile-options-eyebrow"><SlidersHorizontal size={14} /> Profile configuration</div>
                            <h1>Profile options</h1>
                            <p>Organise the values members use across registration, search and profile editing.</p>
                        </div>
                        <div className="profile-options-summary">
                            <span><BadgeCheck size={17} /><b>{options.length}</b><small>Active in {categoryConfig.label}</small></span>
                            <span><Lightbulb size={17} /><b>{pending.length}</b><small>Suggestions waiting</small></span>
                        </div>
                    </header>

                    <div className="profile-options-tabs">
                        <button className={activeTab === 'manage' ? 'is-active' : ''} onClick={() => setActiveTab('manage')}><SlidersHorizontal size={15} /> Manage options</button>
                        <button className={activeTab === 'pending' ? 'is-active' : ''} onClick={() => setActiveTab('pending')}>
                            <Lightbulb size={15} /> Pending suggestions {pending.length > 0 && <b>{pending.length}</b>}
                        </button>
                    </div>

                    {activeTab === 'manage' ? (
                        <section className="profile-options-workspace">
                            <nav className="profile-options-nav" aria-label="Profile option categories">
                                <div className="profile-options-nav__label">OPTION GROUPS</div>
                                {CATEGORIES.map(item => (
                                    <button key={item.key} className={category === item.key ? 'is-active' : ''} onClick={() => selectCategory(item.key)}>
                                        <span><item.Icon size={17} /></span>
                                        <div><b>{item.label}</b><small>{item.description}</small></div>
                                        <ChevronRight size={15} />
                                    </button>
                                ))}
                            </nav>

                            <div className="profile-options-main">
                                <div className="profile-options-main__header">
                                    <div className="profile-options-category-icon"><categoryConfig.Icon size={20} /></div>
                                    <div><h2>{categoryConfig.label}</h2><p>{categoryConfig.description}</p></div>
                                    <span>{options.length} options</span>
                                </div>

                                {(category === 'caste' || category === 'subcaste') && (
                                    <div className="profile-options-cascade">
                                        <div><Layers3 size={16} /><span><b>Parent selection</b><small>Choose where these options belong</small></span></div>
                                        <label><span>Religion</span><Select value={religionParent} onChange={value => { setReligionParent(value); setCasteParent(''); }} options={religionChoices} placeholder="Select religion" ariaLabel="Religion" /></label>
                                        {category === 'subcaste' && religionParent && <label><span>Caste</span><Select value={casteParent} onChange={setCasteParent} options={casteChoices} placeholder="Select caste" ariaLabel="Caste" /></label>}
                                    </div>
                                )}

                                {!cascadeReady() ? (
                                    <div className="profile-options-empty"><Layers3 size={25} /><b>Select the parent fields first</b><span>The matching options will appear here.</span></div>
                                ) : (
                                    <>
                                        <form className="profile-options-add" onSubmit={handleAdd}>
                                            <div className="profile-options-add__label"><Plus size={16} /><span><b>Add {categoryConfig.label}</b><small>Create a new member-facing option</small></span></div>
                                            {category === 'job' && (
                                                <div className="profile-options-group-select"><span>Profession category</span><Select value={occupationGroup} onChange={setOccupationGroup} options={OCCUPATION_GROUPS.map(group => ({ value: group.key, label: group.label }))} allowEmpty={false} ariaLabel="Profession category" /></div>
                                            )}
                                            <input placeholder={`Enter ${categoryConfig.label.toLowerCase()} name`} value={newValue} onChange={event => setNewValue(event.target.value)} />
                                            <button type="submit" disabled={!newValue.trim()}><Plus size={16} /> Add option</button>
                                        </form>

                                        <div className="profile-options-toolbar">
                                            <label><Search size={16} /><input placeholder={`Search ${categoryConfig.label.toLowerCase()}…`} value={search} onChange={event => setSearch(event.target.value)} />{search && <button onClick={() => setSearch('')}><X size={13} /></button>}</label>
                                            <span>Showing <b>{visibleOptions.length}</b> of {options.length}</span>
                                        </div>

                                        {loading ? (
                                            <div className="profile-options-empty"><BrandLoader size={36} /><b>Loading options</b></div>
                                        ) : options.length === 0 ? (
                                            <div className="profile-options-empty"><Tag size={25} /><b>No options yet</b><span>Add the first option above.</span></div>
                                        ) : visibleOptions.length === 0 ? (
                                            <div className="profile-options-empty"><Search size={25} /><b>No matches found</b><span>Try a different search term.</span></div>
                                        ) : category === 'job' ? (
                                            <div className="occupation-groups">
                                                {occupationSections.filter(group => group.options.length).map(group => (
                                                    <section className="occupation-group" key={group.key}>
                                                        <header><span><group.Icon size={17} /></span><div><h3>{group.label}</h3><p>{group.total} occupation{group.total === 1 ? '' : 's'}</p></div></header>
                                                        <div className="profile-options-grid">{group.options.map(renderOption)}</div>
                                                    </section>
                                                ))}
                                            </div>
                                        ) : (
                                            <div className="profile-options-grid">{visibleOptions.map(renderOption)}</div>
                                        )}
                                    </>
                                )}
                            </div>
                        </section>
                    ) : (
                        <section className="profile-options-pending">
                            <div className="profile-options-pending__header"><span><Lightbulb size={20} /></span><div><h2>Pending suggestions</h2><p>Review values suggested when members select “Other”.</p></div><b>{pending.length} waiting</b></div>
                            {pendingLoading ? (
                                <div className="profile-options-empty"><BrandLoader size={36} /><b>Loading suggestions</b></div>
                            ) : pending.length === 0 ? (
                                <div className="profile-options-empty profile-options-empty--large"><Check size={26} /><b>You are all caught up</b><span>New member suggestions will appear here.</span></div>
                            ) : (
                                <div className="profile-options-pending__list">
                                    {pending.map(item => (
                                        <article key={item._id}>
                                            <div className="profile-options-pending__icon"><Lightbulb size={17} /></div>
                                            <div className="profile-options-pending__value">
                                                <div className="profile-options-pending__name">
                                                    <span className="profile-options-pending__tag">{PENDING_CATEGORIES.find(entry => entry.key === item.category)?.label || item.category}</span>
                                                    {pendingEditingId === item._id ? (
                                                        <input
                                                            className="profile-options-pending__edit-input"
                                                            autoFocus
                                                            value={pendingEditingValue}
                                                            onChange={event => setPendingEditingValue(event.target.value)}
                                                            onKeyDown={event => {
                                                                if (event.key === 'Enter') handleSavePendingEdit(item);
                                                                if (event.key === 'Escape') cancelPendingEdit();
                                                            }}
                                                            aria-label={`Edit ${item.value}`}
                                                        />
                                                    ) : <h3>{item.value}</h3>}
                                                </div>
                                                <p>{item.parent && <>Under {item.parent.replace('|', ' → ')} · </>}{item.suggestedByName ? `Suggested by ${item.suggestedByName}` : 'Suggested by a member'} · {new Date(item.createdAt).toLocaleDateString('en-IN')}</p>
                                            </div>
                                            <div className="profile-options-pending__actions">
                                                {pendingEditingId === item._id ? (
                                                    <>
                                                        <button className="is-save" onClick={() => handleSavePendingEdit(item)} disabled={pendingSaving || !pendingEditingValue.trim()}><Check size={15} /> {pendingSaving ? 'Saving...' : 'Save'}</button>
                                                        <button className="is-cancel" onClick={cancelPendingEdit} disabled={pendingSaving}><X size={15} /> Cancel</button>
                                                    </>
                                                ) : (
                                                    <>
                                                        <button className="is-edit" onClick={() => startPendingEdit(item)}><Pencil size={14} /> Edit</button>
                                                        <button className="is-approve" onClick={() => handleApprove(item)}><Check size={15} /> Approve</button>
                                                        <button className="is-reject" onClick={() => setPendingConfirm({ type: 'reject', id: item._id, value: item.value })}><X size={15} /> Reject</button>
                                                    </>
                                                )}
                                            </div>
                                        </article>
                                    ))}
                                </div>
                            )}
                        </section>
                    )}
                </div>
            </main>

            <ConfirmDialog
                open={Boolean(pendingConfirm)}
                title={pendingConfirm?.type === 'reject' ? 'Reject Suggestion?' : 'Remove Option?'}
                message={pendingConfirm ? `${pendingConfirm.type === 'reject' ? 'Reject' : 'Remove'} “${pendingConfirm.value}”?` : ''}
                items={pendingConfirm?.type === 'reject' ? ['This permanently deletes the suggestion'] : ['Existing profiles keep this value', 'It is removed from future dropdown choices']}
                confirmLabel={pendingConfirm?.type === 'reject' ? 'Reject' : 'Remove'}
                danger
                onConfirm={pendingConfirm?.type === 'reject' ? confirmRejectSuggestion : confirmDeleteOption}
                onCancel={() => setPendingConfirm(null)}
            />
        </div>
    );
};

export default ProfileOptions;
